import { Slider } from "@classdojo/web/nessie";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { autoTranslate } from "@web-monorepo/vite-auto-translate-plugin/runtime";
import React from "react";
import permissionErrorLogo from "src/pods/noise/assets/mic_error.png";
import errorMessages, { ErrorType } from "src/pods/noise/data/errorMessages";
import logEvent from "src/utils/logEvent";

const MAX_BAR_HEIGHT = 448;

type NoiseDisplayProps = {
  sendNoise?: (payload: { gain: number }) => void;
  gain?: number;
  showSensitivityInput?: boolean;
  onPermissionError?: (errorName: string | null) => void;
};

type NoiseDisplayState = {
  hasPermission: boolean | null;
  frequencies: number[] | null;
  isTooLoud: boolean;
  permissionError: ErrorType | null;
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class NoiseDisplay extends React.Component<NoiseDisplayProps, NoiseDisplayState> {
  constructor(props: NoiseDisplayProps) {
    super(props);
    this.state = {
      hasPermission: null,
      frequencies: null,
      isTooLoud: false,
      permissionError: null,
    };
  }

  componentDidMount() {
    this.start();
    logEvent({ eventName: "noise.result.open" });
  }

  componentWillUnmount() {
    this.stop();
    logEvent({ eventName: "noise.result.close" });
  }

  render() {
    const { hasPermission, frequencies, permissionError } = this.state;
    const bars = frequencies && frequencies.slice(0, Math.round(frequencies.length / 4)); // use the lower 1/4 of the frequency spectrum

    return (
      <div sx={displayStyle}>
        {hasPermission && (
          <div>
            <div sx={barsStyle}>
              <span sx={fillerBarStyle} />
              {bars &&
                bars.map((frequency, idx) => (
                  <span
                    key={idx}
                    sx={{
                      ...barStyle,
                      height: `${Math.max(MAX_BAR_HEIGHT * (frequency / 256), 10)}px`,
                    }}
                  />
                ))}
            </div>
            {this.props.showSensitivityInput && (
              <Slider
                aria-label={autoTranslate("Noise sensitivity range")}
                label={autoTranslate("Sensitivity")}
                value={[this.props.gain || 0]}
                min={-125}
                max={125}
                step={1}
                onChange={(value) => this.changeSensitivity(value[0])}
                sx={{
                  marginTop: "l",
                }}
                theme="light"
                focusColor="#2c2a50"
              />
            )}
          </div>
        )}
        {!hasPermission && (
          <div sx={permissionBoxStyle}>
            <img src={permissionErrorLogo} sx={permissionErrorLogoStyle} alt="" />
            <div sx={permissionText}>
              {(permissionError === null ? "" : errorMessages(permissionError)) ||
                autoTranslate("To use Noise Meter, please allow use of your microphone.")}
            </div>
          </div>
        )}
      </div>
    );
  }

  microphoneContext: {
    destroy: VoidFunction;
  } | null = null;
  tooLoudTimer: NodeJS.Timeout | null = null;

  start() {
    const setFrequencies = (frequencies: number[] | null) => {
      this.setState({ frequencies });

      if (frequencies) {
        const isTooLoud = frequencies.reduce((total, bar) => total + bar, 0) / frequencies.length >= 64;
        if (isTooLoud) {
          if (this.tooLoudTimer) clearTimeout(this.tooLoudTimer);
          this.tooLoudTimer = setTimeout(() => this.setState({ isTooLoud: false }), 3000);
          this.setState({ isTooLoud: true });
        }
      } else {
        this.setState({ isTooLoud: false });
      }
    };

    this.stop();

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        this.setState({ hasPermission: true, permissionError: null });
        this.props.onPermissionError?.(null);

        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 64;
        analyser.smoothingTimeConstant = 0.3;
        const audioBins = new Uint8Array(analyser.frequencyBinCount);
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        const updateFrequencies = () => {
          const gain = 1 + (this.props.gain || 0) / 255;
          analyser.getByteFrequencyData(audioBins);
          const frequencies = Array.from(audioBins).map((bin) => Math.min(Math.max(Math.round(bin * gain), 0), 255));
          setFrequencies(frequencies);
        };

        // const processor = audioContext.createScriptProcessor(1024, 1, 1);
        // processor.addEventListener("audioprocess", updateVolume, false);
        // processor.connect(audioContext.destination);
        const timer = setInterval(updateFrequencies, 60);

        function destroy() {
          Array.from(stream.getAudioTracks()).forEach((track) => {
            track.stop();
            track.removeEventListener("ended", destroy);
          });
          source.disconnect(0);
          clearInterval(timer);
          // processor.removeEventListener("audioprocess", updateVolume);
          // processor.disconnect(0);
          setFrequencies(null);
        }

        Array.from(stream.getAudioTracks()).forEach((track) => track.addEventListener("ended", destroy, false));

        this.microphoneContext = {
          destroy,
        };
      })
      .catch((err) => {
        if (!(err instanceof Error)) throw err;
        let errorName = err.name;
        if (!errorName || errorName === "Error") {
          if (/not implemented/.test(err.message)) {
            errorName = "NotSupportedError";
          } else {
            errorName = "UnknownError";
          }
        }
        this.setState({ hasPermission: false, permissionError: errorName as ErrorType });
        this.props.onPermissionError?.(errorName);
        logEvent({ eventName: "noise.unsupported" });
      });
  }

  stop() {
    if (this.microphoneContext) {
      this.microphoneContext.destroy();
      this.microphoneContext = null;
    }
  }

  changeSensitivity(gain: number) {
    this.props.sendNoise?.({ gain });
  }
}

const displayStyle: ThemeUIStyleObject = {
  textAlign: "center",
};

const permissionBoxStyle: ThemeUIStyleObject = {
  width: "436px",
  margin: RAW_cssValue("auto"),
  padding: RAW_cssValue("85px 0 72px 0"),
  borderRadius: "dt_radius_s",
  backgroundColor: RAW_cssValue("#fff"),
};

const permissionErrorLogoStyle: ThemeUIStyleObject = {
  marginBottom: "dt_xl",
};

const permissionText: ThemeUIStyleObject = {
  maxWidth: "283px",
  margin: RAW_cssValue("0 auto"),
  fontSize: "18px",
  textAlign: "center",
  fontWeight: 600,
  color: RAW_cssValue("#2c2f42"),
};

const barsStyle = {
  height: `${MAX_BAR_HEIGHT}px`,
};

const fillerBarStyle = {
  display: "inline-block",
  height: `${MAX_BAR_HEIGHT}px`,
};

const barStyle: ThemeUIStyleObject = {
  display: "inline-block",
  width: "70px",
  "@media (max-width: 650px)": {
    width: "40px",
  },
  "@media (max-width: 520px)": {
    width: "15px",
  },
  margin: RAW_cssValue("0 10px"),
  borderRadius: "dt_radius_s",
  verticalAlign: "bottom",
  background: `linear-gradient(to top, rgb(150,199,72) 0px, rgb(255,190,41) ${Math.round(
    MAX_BAR_HEIGHT / 3,
  )}px, rgb(252,88,79) ${Math.round(MAX_BAR_HEIGHT / 3) * 2}px)`,
  willChange: "height",
  transition: "height 60ms",
};
